/**
 * Report page format - per-user Page Setup preferences.
 *
 * Wraps a subset of Syncfusion's SectionFormatProperties that most users
 * care about: page size, margins, header/footer distance. Persisted as
 * user_settings.reportPageFormat (JSON-stringified), scope 'user' or
 * 'clinic'.
 *
 * The values in this file are all in POINTS (1/72 inch). That's the unit
 * the Document Editor stores natively and expects in setDefaultSectionFormat
 * (see help.syncfusion.com/document-processing/word/word-processor/react/
 * how-to/set-default-format-in-document-editor/).
 *
 * Application points (three call sites in ReportEditor):
 *   1. Container `created` event  -> setDefaultSectionFormat(format)
 *      Catches openBlank() and any fresh document creation.
 *   2. insertHeader / buildHeaderDocument in insertHeader.ts
 *      Baked into the SFDT for new reports so the sectionFormat is
 *      explicit even before setDefaultSectionFormat can fire.
 *   3. applyTemplate after openAsync + paste -> selection.sectionFormat = ...
 *      Overwrites the section format the template body may have shipped
 *      with (Syncfusion's paste() only inserts blocks, not section format,
 *      so we mutate the base doc's section explicitly).
 */
import { loadSettingsFull, saveUserSettings, SettingScope } from './userSettings';

/**
 * Everything the Syncfusion Page Setup dialog can set, verbatim. Field
 * names match Syncfusion's SectionFormatProperties exactly so we can pass
 * the whole object to setDefaultSectionFormat() and copy field-by-field
 * onto selection.sectionFormat with no translation layer.
 *
 * Grouped by which tab of the Page Setup dialog owns the field, so future
 * additions to the UI stay obvious:
 *   Margins tab: leftMargin, rightMargin, topMargin, bottomMargin
 *   Paper tab:   pageWidth, pageHeight
 *   Layout tab:  headerDistance, footerDistance,
 *                differentFirstPage, differentOddAndEvenPages,
 *                numberOfColumns, equalWidth, lineBetweenColumns
 */
export interface PageFormat {
  // ---- Paper tab ----
  /** Page width in points. 612 = US Letter, 595 = A4, etc. */
  pageWidth: number;
  /** Page height in points. 792 = US Letter, 842 = A4, etc. */
  pageHeight: number;

  // ---- Margins tab ----
  leftMargin: number;
  rightMargin: number;
  topMargin: number;
  bottomMargin: number;

  // ---- Layout tab ----
  headerDistance: number;
  footerDistance: number;
  /** True = first page uses a separate header/footer definition. */
  differentFirstPage: boolean;
  /** True = odd and even pages use different header/footer definitions. */
  differentOddAndEvenPages: boolean;
  /** 1 = single column (default). Higher = multi-column body. */
  numberOfColumns: number;
  /** When numberOfColumns > 1, true = auto-equal widths, false = custom. */
  equalWidth: boolean;
  /** Draws a vertical rule between columns. Only meaningful when numberOfColumns > 1. */
  lineBetweenColumns: boolean;
}

export interface PresetPaperSize {
  key: string;
  label: string;
  pageWidth: number;
  pageHeight: number;
}

/** Common paper sizes for radiology reports. Values in points (1/72"). */
export const PAPER_SIZES: PresetPaperSize[] = [
  { key: 'letter', label: 'Letter (8.5" x 11")',    pageWidth: 612, pageHeight: 792 },
  { key: 'a4',     label: 'A4 (210 x 297 mm)',      pageWidth: 595, pageHeight: 842 },
  { key: 'legal',  label: 'Legal (8.5" x 14")',     pageWidth: 612, pageHeight: 1008 },
  { key: 'a5',     label: 'A5 (148 x 210 mm)',      pageWidth: 420, pageHeight: 595 },
  { key: 'a3',     label: 'A3 (297 x 420 mm)',      pageWidth: 842, pageHeight: 1191 },
];

/** US Letter with 1" margins, single column, no odd/even split -
 *  historical EdgeWeb default matching a plain MS Word letterhead. */
export const DEFAULT_PAGE_FORMAT: PageFormat = {
  pageWidth: 612,
  pageHeight: 792,
  leftMargin: 72,
  rightMargin: 72,
  topMargin: 72,
  bottomMargin: 72,
  headerDistance: 36,
  footerDistance: 36,
  differentFirstPage: false,
  differentOddAndEvenPages: false,
  numberOfColumns: 1,
  equalWidth: true,
  lineBetweenColumns: false,
};

/** Match a (pageWidth, pageHeight) pair to a known paper-size key, or
 *  'custom' if none match. Tolerant to swapped orientation (portrait vs
 *  landscape) - matches by min/max so a landscape A4 still resolves. */
export function matchPaperSize(pageWidth: number, pageHeight: number): string {
  for (const p of PAPER_SIZES) {
    const [a, b] = [Math.min(pageWidth, pageHeight), Math.max(pageWidth, pageHeight)];
    const [pa, pb] = [Math.min(p.pageWidth, p.pageHeight), Math.max(p.pageWidth, p.pageHeight)];
    if (Math.abs(a - pa) < 2 && Math.abs(b - pb) < 2) return p.key;
  }
  return 'custom';
}

/** Fill any missing fields on a partial PageFormat with the DEFAULT values.
 *  Used when loading a partial JSON payload from an older client version. */
export function migratePageFormat(partial: Partial<PageFormat> | null | undefined): PageFormat {
  if (!partial || typeof partial !== 'object') return { ...DEFAULT_PAGE_FORMAT };
  return {
    ...DEFAULT_PAGE_FORMAT,
    ...partial,
  };
}

const SETTINGS_KEY = 'reportPageFormat';

export async function loadPageFormat(): Promise<{ format: PageFormat; scope?: SettingScope }> {
  const s = await loadSettingsFull();
  const raw = (s.values as any)[SETTINGS_KEY] as string | undefined;
  if (!raw) return { format: { ...DEFAULT_PAGE_FORMAT }, scope: undefined };
  try {
    const parsed = JSON.parse(raw);
    return {
      format: migratePageFormat(parsed),
      scope: (s.scope as any)[SETTINGS_KEY],
    };
  } catch {
    return { format: { ...DEFAULT_PAGE_FORMAT }, scope: (s.scope as any)[SETTINGS_KEY] };
  }
}

export async function savePageFormat(format: PageFormat, scope: SettingScope): Promise<boolean> {
  return saveUserSettings({ [SETTINGS_KEY]: JSON.stringify(format) } as any, scope);
}

/**
 * Write a PageFormat onto the editor's CURRENT section format. Used after
 * loading an existing saved report / template so the display picks up the
 * user's current default even though the saved SFDT was serialized with
 * older geometry.
 *
 * Deliberately mutates every field so a half-populated saved section
 * doesn't leak stale values into the display. Wrapped in try/catch so a
 * missing selection (unmounted editor, timing edge) fails silently instead
 * of blowing up the entire report open.
 */
export function applyPageFormatToCurrentSection(
  container: { documentEditor?: any } | null,
  format: PageFormat | null | undefined,
): void {
  if (!container || !format) return;
  try {
    const sf: any = container.documentEditor?.selection?.sectionFormat;
    if (!sf) return;
    // Paper tab
    sf.pageWidth  = format.pageWidth;
    sf.pageHeight = format.pageHeight;
    // Margins tab
    sf.leftMargin    = format.leftMargin;
    sf.rightMargin   = format.rightMargin;
    sf.topMargin     = format.topMargin;
    sf.bottomMargin  = format.bottomMargin;
    // Layout tab
    sf.headerDistance           = format.headerDistance;
    sf.footerDistance           = format.footerDistance;
    sf.differentFirstPage       = format.differentFirstPage;
    sf.differentOddAndEvenPages = format.differentOddAndEvenPages;
    sf.numberOfColumns          = format.numberOfColumns;
    sf.equalWidth               = format.equalWidth;
    sf.lineBetweenColumns       = format.lineBetweenColumns;
  } catch (err) {
    console.warn('[pageFormat] applyPageFormatToCurrentSection failed', err);
  }
}

